/**
 * Materiali "nascosti" per organizzazione — filtro di sola vista, reversibile.
 *
 * Nascondere un materiale lo toglie dalle liste della PROPRIA org (tabella
 * materiali + material picker del configuratore) senza cancellarlo: funziona
 * anche sui materiali del catalogo globale/condiviso, che non si possono eliminare.
 * Non tocca preventivi o calcoli già esistenti.
 *
 * useHiddenMaterials(): Set di material id nascosti per l'org corrente
 * useToggleHiddenMaterial(): nascondi/mostra un singolo materiale
 * useSetHiddenMaterials(): nascondi/mostra in blocco (es. tutta una famiglia)
 */
import { useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { toast } from 'sonner';

export const useHiddenMaterials = () => {
  const { currentOrganizationId } = useCurrentOrganization();

  const query = useQuery({
    queryKey: ['hidden-materials', currentOrganizationId],
    enabled: !!currentOrganizationId,
    staleTime: 60_000,
    queryFn: async (): Promise<string[]> => {
      if (!currentOrganizationId) return [];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data, error } = await (supabase as any)
        .from('org_hidden_materials')
        .select('material_id')
        .eq('organization_id', currentOrganizationId);
      if (error) {
        console.error('[useHiddenMaterials] error:', error);
        return [];
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (data ?? []).map((r: any) => r.material_id);
    },
  });

  const hiddenSet = useMemo(() => new Set(query.data ?? []), [query.data]);

  return { hiddenSet, hiddenIds: query.data ?? [], isLoading: query.isLoading };
};

export const useToggleHiddenMaterial = () => {
  const { user } = useAuth();
  const { currentOrganizationId } = useCurrentOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ materialId, isHidden }: { materialId: string; isHidden: boolean }) => {
      if (!currentOrganizationId) throw new Error('Nessuna organizzazione selezionata');
      if (isHidden) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any)
          .from('org_hidden_materials')
          .delete()
          .eq('organization_id', currentOrganizationId)
          .eq('material_id', materialId);
        if (error) throw error;
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any)
          .from('org_hidden_materials')
          .insert({ organization_id: currentOrganizationId, material_id: materialId, hidden_by: user?.id ?? null });
        if (error) throw error;
      }
    },
    onSuccess: (_d, vars) => {
      queryClient.invalidateQueries({ queryKey: ['hidden-materials', currentOrganizationId] });
      toast.success(vars.isHidden ? 'Materiale di nuovo visibile' : 'Materiale nascosto dal tuo catalogo');
    },
    onError: (e: unknown) => {
      const msg = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : String(e);
      toast.error(`Operazione non riuscita: ${msg}`);
    },
  });
};

/**
 * Nascondi/mostra in blocco un insieme di materiali (es. tutta una famiglia).
 * `hide=true` inserisce (ignora quelli già nascosti), `hide=false` rimuove.
 */
export const useSetHiddenMaterials = () => {
  const { user } = useAuth();
  const { currentOrganizationId } = useCurrentOrganization();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ materialIds, hide }: { materialIds: string[]; hide: boolean }) => {
      if (!currentOrganizationId) throw new Error('Nessuna organizzazione selezionata');
      if (materialIds.length === 0) return 0;
      if (hide) {
        const rows = materialIds.map((material_id) => ({
          organization_id: currentOrganizationId,
          material_id,
          hidden_by: user?.id ?? null,
        }));
        // upsert: ignora i duplicati (già nascosti) grazie al vincolo UNIQUE
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any)
          .from('org_hidden_materials')
          .upsert(rows, { onConflict: 'organization_id,material_id', ignoreDuplicates: true });
        if (error) throw error;
      } else {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { error } = await (supabase as any)
          .from('org_hidden_materials')
          .delete()
          .eq('organization_id', currentOrganizationId)
          .in('material_id', materialIds);
        if (error) throw error;
      }
      return materialIds.length;
    },
    onSuccess: (n, vars) => {
      queryClient.invalidateQueries({ queryKey: ['hidden-materials', currentOrganizationId] });
      toast.success(vars.hide ? `${n} materiali nascosti` : `${n} materiali di nuovo visibili`);
    },
    onError: (e: unknown) => {
      const msg = e && typeof e === 'object' && 'message' in e ? String((e as { message: unknown }).message) : String(e);
      toast.error(`Operazione non riuscita: ${msg}`);
    },
  });
};
